/**
 * advanceState.ts — one snapshot of an operational expense advance in, the whole rail out.
 *
 * `setupState.ts` FOR A `loans` ROW, AND ITS RULES RATHER THAN NEW ONES. The marks, the row classes
 * (gate / render / record), the voices, the two counter predicates and the narrator's precedence
 * are the contract dossier's, imported rather than restated — two spellings of "is this row done"
 * is precisely how the previous screen came to draw `Insurance 3/4` over four empty rows.
 *
 * WHAT IS NEW IS THE SUBJECT AND NOTHING ELSE. An advance has three areas, ten rows, no activation
 * ceremony and no server status payload: its gates are the driver's hire purchase signature [D-B55]
 * and the seal on its own Schedule, and both are facts this app already derives for the board.
 *
 * THE SEAL PREDICATE IS THE HOUSE'S, LIFTED RATHER THAN RE-ASKED. `isSealedForActivation` in
 * `setupState.ts` is `legacy signed_at` AND a non-empty `agreement_url`, which is what
 * `sign_contract` itself consumes, and it is the one truth for an in-app seal and a paper original
 * alike — the 161 production advances are all paper [F3] and every one of them is executed.
 *
 * PURE, AND REACT-FREE. `now` is an input, so the same snapshot always produces the same rail —
 * which is what makes the seal timer and the narrator testable at all.
 */

import { differenceInSeconds, format, isValid, parseISO } from 'date-fns';

import { businessDay, formatCurrency } from '@/lib/format';

import {
    isCountedRow,
    isRowSettled,
    type AreaGroup,
    type DocumentFact,
    type ManifestRow,
    type Narrator,
    type NarratorAction,
    type RowClass,
    type RowMeta,
    type RowState,
    type SetupProgress,
    type SignatureFact,
} from '../setupState';
import { ADVANCE_AREAS, type AdvanceArea } from './advanceSteps';

// ─── The row vocabulary ──────────────────────────────────────────────────────

/**
 * The ten rows of an advance.
 *
 * IDS ARE STABLE AND PUBLIC, for `ManifestRowId`'s reason: deep links focus them, the narrator
 * names them, and the canvas blocks below carry the same strings as DOM ids.
 */
export type AdvanceRowId =
    | 'adv-transaction'
    | 'adv-rate'
    | 'adv-purpose'
    | 'adv-weekly'
    | 'adv-first'
    | 'adv-parties'
    | 'adv-hp-reference'
    | 'adv-hp-signature'
    | 'adv-document'
    | 'adv-sealed';

export type AdvanceRow = ManifestRow<AdvanceArea, AdvanceRowId>;
export type AdvanceAreaGroup = AreaGroup<AdvanceArea, AdvanceRowId>;
export type AdvanceNarrator = Narrator<AdvanceArea, AdvanceRowId>;
export type AdvanceNarratorAction = NarratorAction<AdvanceArea, AdvanceRowId>;

interface AdvanceRowDef {
    id: AdvanceRowId;
    area: AdvanceArea;
    label: string;
    cls: RowClass;
    block: string;
    focus?: string;
}

const ROW_DEFS: AdvanceRowDef[] = [
    // 1 ── Disbursement
    { id: 'adv-transaction', area: 'disbursement', label: 'Disbursement', cls: 'render', block: 'b-disbursement', focus: 'f-transaction' },
    // THE RATE IS A RECORD AND ZERO IS A VALUE. An interest-free advance is a real arrangement —
    // the column is `NOT NULL CHECK >= 0` and the Schedule prints "0 per cent" without complaint —
    // so a row that read 0 as "not filled in" would ask an admin to correct a deliberate figure.
    { id: 'adv-rate', area: 'disbursement', label: 'Interest rate', cls: 'record', block: 'b-disbursement', focus: 'f-rate' },
    // RENDER-BLOCKING, AND THE ONLY FIELD ON THIS FLOW THAT IS TYPED RATHER THAN PICKED. The
    // Schedule's first particular is "What this advance is for", resolved from `loans.description`
    // through `NULLIF(btrim(...), '')` — a blank one holds the document.
    { id: 'adv-purpose', area: 'disbursement', label: 'Purpose', cls: 'render', block: 'b-purpose', focus: 'f-purpose' },

    // 2 ── Repayment
    { id: 'adv-weekly', area: 'repayment', label: 'Weekly repayment', cls: 'render', block: 'b-repayment', focus: 'f-weekly' },
    { id: 'adv-first', area: 'repayment', label: 'First repayment', cls: 'render', block: 'b-repayment', focus: 'f-first' },

    // 3 ── Agreement
    { id: 'adv-parties', area: 'agreement', label: 'Parties', cls: 'render', block: 'b-parties' },
    { id: 'adv-hp-reference', area: 'agreement', label: 'Hire purchase reference', cls: 'render', block: 'b-advanceagr' },
    { id: 'adv-hp-signature', area: 'agreement', label: 'Hire purchase signature', cls: 'gate', block: 'b-advanceagr' },
    { id: 'adv-document', area: 'agreement', label: 'Advance agreement', cls: 'render', block: 'b-advanceagr' },
    { id: 'adv-sealed', area: 'agreement', label: 'Executed', cls: 'gate', block: 'b-advanceagr' },
];

/** The receipt a satisfied group collapses to. */
const AREA_RECEIPTS: Record<AdvanceArea, string> = {
    disbursement: 'Recorded',
    repayment: 'Scheduled',
    agreement: 'Executed',
};

// ─── The snapshot every row is derived from ──────────────────────────────────

/**
 * WHY the repayment pair is a read rather than a pair of fields [D-B54].
 *
 * `'below-watermark'` is the established fact: this advance's id is under
 * `vehicle_recovery_gate.below_id` and it carries no `loan_instalments` row, so it is settled at
 * completion and a weekly figure added now would be billed from the day the money was disbursed.
 * `'watermark-unknown'` is the SAME shut door for a different reason — the watermark row was absent
 * or its read was refused — and it must not be described as the first, because that states a
 * repayment regime the app has not established.
 *
 * `'board-unreadable'` is the third, and it is the one that is not about this advance at all: the
 * instalment rows are read off the agreements board's subjects, so a board read that was REFUSED
 * hands this rail an empty array that looks exactly like an advance with no schedule. 114 of the
 * 161 production advances carry one, and every one of them is below the watermark — so without
 * this reason a refused read paints "Settled at completion" over a live weekly repayment.
 */
export type AdvanceLegacyReason = 'below-watermark' | 'watermark-unknown' | 'board-unreadable';

/** What the legacy question is answered from — the hook holds the reads, this holds the rule. */
export interface AdvanceLegacyInput {
    isNew: boolean;
    loanId: number | 'new';
    /** `loan_instalments` rows the board's subjects carried for this advance. */
    instalmentCount: number;
    /** `vehicle_recovery_gate.below_id`, or null/undefined where the row is absent or unread. */
    watermark: number | null | undefined;
    watermarkFailed: boolean;
    /** The agreements board's own `failed` — contract, subjects or documents refused. */
    boardFailed: boolean;
}

/**
 * IS THIS ADVANCE'S REPAYMENT A READ, AND WHY — the whole rule, in one place and pure.
 *
 * THE BOARD IS ASKED FIRST, BEFORE THE INSTALMENT COUNT, because under a refused board read the
 * count is not evidence of anything: `subjectLoan` is null, `instalments` is `[]`, and an advance
 * that is on a weekly schedule would fall straight through to `'below-watermark'` and be told it
 * predates the rule. Zero rows only means "no schedule" once the read that would have carried them
 * has actually answered.
 *
 * A NEW ADVANCE IS NEVER LEGACY: its id does not exist yet and it is created into the new arm by
 * definition.
 */
export function advanceLegacyReason(input: AdvanceLegacyInput): AdvanceLegacyReason | false {
    const { isNew, loanId, instalmentCount, watermark, watermarkFailed, boardFailed } = input;
    if (isNew || typeof loanId !== 'number') return false;
    if (boardFailed) return 'board-unreadable';
    if (instalmentCount > 0) return false;
    if (watermarkFailed || watermark === null || watermark === undefined) return 'watermark-unknown';
    return loanId < watermark ? 'below-watermark' : false;
}

/** The `loans` row itself — null while the advance is still a draft. */
export interface AdvanceLoanFacts {
    id: number;
    /** `loans.description` — the Schedule's `{{loan.purpose}}`, and render-blocking. */
    purpose: string | null;
    /** `loans.interest_rate` — per cent, charged once. NOT NULL in SQL, so 0 is a figure. */
    interestRate: number | null;
    transactionId: number | null;
}

/** The disbursement this advance names. The PRINCIPAL is the transaction's amount. */
export interface AdvanceTransactionFact {
    id: number;
    amount: number | null;
    /** `transactions.transaction_at` — an instant. */
    transactionAt: string | null;
}

/**
 * One `loan_instalments` row.
 *
 * `starts_at` IS A DATE COLUMN, so PostgREST hands over `'YYYY-MM-DD'` and a STRING comparison is
 * the chronological one — no parse, no zone, and no host clock moving a boundary.
 */
export interface AdvanceInstalmentFact {
    amount: number | null;
    startsAt: string | null;
}

/** The borrower, as the Schedule's Parties table prints them. */
export interface AdvanceDriverFacts {
    fullName: string | null;
    phone: string | null;
    address: string | null;
}

/** The vehicle, as the Schedule's Related agreement row prints it. */
export interface AdvanceVehicleFacts {
    make: string | null;
    model: string | null;
    registration: string | null;
}

/**
 * The LOAN slot as the board derived it, carried rather than re-derived.
 *
 * `buildBoardSlots` mints this row from the same `loan_agreements` and `agreement_documents` rows
 * `agreement_sign` reads; copying it here is what keeps the rail and the board — which is drawn
 * INSIDE the agreement area — from answering the same question two ways.
 */
export interface AdvanceSlotFact {
    /** `loan_agreements.signed_at` — the paper original's date, or the seal's. */
    legacySignedAt: string | null;
    /** `loan_agreements.agreement_url` — the filed PDF. Both halves are the seal. */
    legacyUrl: string | null;
    /**
     * `loan_agreements.origin` — NULL or `'SCANNED'` is the paper original [F3].
     *
     * IT IS WHAT MAKES A PAPER ADVANCE READ AS ONE. `agreement_create` refuses every one of the 161
     * existing advances with `AGREEMENT_ALREADY_SCANNED`, so the Agreement area on them is a READ
     * of the paper record and never a ceremony that could be started.
     */
    legacyOrigin: string | null;
    document: DocumentFact | null;
    /** The board's own lock sentence (`advanceLockedFor`), or null. */
    blockedReason: string | null;
}

export interface AdvanceSnapshot {
    contractId: number;
    /** The clock. Every elapsed figure is measured from here, which is what makes this pure. */
    now?: Date | string;
    /** The create flow: nothing is saved yet, and every row reads off `drafts`. */
    isNew: boolean;
    /** The saved `loans` row. Null on a new advance. */
    loan: AdvanceLoanFacts | null;
    /** `loan_instalments`, as read. Empty where there is no schedule. */
    instalments: AdvanceInstalmentFact[];
    /** The disbursement `loans.transaction_id` names. */
    transaction: AdvanceTransactionFact | null;
    driver: AdvanceDriverFacts;
    vehicle: AdvanceVehicleFacts;
    /**
     * `contract.hire_purchase_reference` as the server would resolve it — the live HP document's
     * designation, else the paper one [D-B52]. Null is "this contract's hire purchase agreement has
     * no number yet", which is a Schedule that would print the merge key literally [F0].
     */
    hpReference: string | null;
    /** The driver has signed the hire purchase agreement — a paper row, or a signed in-app seat. */
    hpDriverSigned: boolean;
    /** The witness has signed the witness agreement — the other arm of the countersign gate. */
    witnessSigned: boolean;
    slot: AdvanceSlotFact | null;
    /** What `agreement_render` says the wording is still missing, once a document exists. */
    renderMissing: string[] | null;
    /**
     * D-B54 — BELOW THE RECOVERY WATERMARK AND NEVER SCHEDULED, so the repayment area is a READ.
     *
     * Decided by the data hook, not here: it is `loans.id < vehicle_recovery_gate.below_id` AND no
     * `loan_instalments` row, and the first half is a table this rail does not read. Adding a
     * schedule to one of these admits it to the allocator at every repayment since disbursement and
     * re-splits history — 32 contracts and ~GHS 139,858 of it — so the door is shut rather than
     * guarded.
     *
     * IT CARRIES ITS REASON BECAUSE THE TWO REASONS ARE NOT THE SAME SENTENCE. The hook fails
     * conservative — a watermark row that is absent or whose read was refused leaves the advance in
     * this arm too — and that is the right DOOR but the wrong COPY: an unscheduled advance above
     * the watermark told "this advance predates the weekly-schedule rule" is being told something
     * about its money that the app has not established. The door is shut either way; only the
     * sentence differs.
     */
    legacyNoSchedule: AdvanceLegacyReason | false;
    /**
     * The Schedule is frozen — `agreement_documents.frozen_at`, set at the FIRST signature.
     *
     * The figures the document printed cannot move underneath it: the disbursement and repayment
     * areas render read-only, and the correction is void-and-reissue on the agreement row.
     */
    frozen: boolean;
    /** Unsaved local edits, by row. Shown as a modifier; never counted as progress. */
    drafts?: Partial<Record<AdvanceRowId, string>>;
}

export interface AdvanceState {
    rows: AdvanceRow[];
    areaGroups: AdvanceAreaGroup[];
    progress: SetupProgress;
    narrator: AdvanceNarrator;
}

// ─── Small helpers, the twins of `setupState`'s ──────────────────────────────

function asDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : parseISO(value);
    return isValid(date) ? date : null;
}

/** The app's one date shape, read on the Ghana business day. */
function dateText(value: string | Date | null | undefined): string {
    const date = businessDay(value);
    return date ? format(date, 'MMM d, yyyy') : '';
}

function money(amount: number | null | undefined): string {
    return amount === null || amount === undefined ? '' : formatCurrency(amount);
}

function isFilled(value: string | null | undefined): boolean {
    return !!value && value.trim().length > 0;
}

/** The server gate on an agreement: a signature date AND a stored PDF. Both halves, always. */
export function isAdvanceSealed(slot: AdvanceSlotFact | null): boolean {
    return !!slot && !!slot.legacySignedAt && isFilled(slot.legacyUrl);
}

/**
 * A PAPER ADVANCE [F3] — signed off the app and permanently so.
 *
 * `agreement_create` refuses a loan whose `loan_agreements` row is signed with
 * `COALESCE(origin,'SCANNED') = 'SCANNED'`, which is all 161 of them, so this is not a state the
 * flow can leave: the Agreement area reads the record and offers no generation.
 */
export function isPaperAdvance(slot: AdvanceSlotFact | null): boolean {
    if (!slot || !slot.legacySignedAt) return false;
    return (slot.legacyOrigin ?? 'SCANNED').toUpperCase() === 'SCANNED';
}

const SIGNED_STATUSES = new Set(['SIGNED']);

function signedCount(document: DocumentFact | null): number {
    return (document?.signatures ?? []).filter((row) => SIGNED_STATUSES.has(row.status)).length;
}

/** The next party in signing order who has not signed. Signature order is the template's. */
function pendingSignature(document: DocumentFact | null): SignatureFact | null {
    return (
        (document?.signatures ?? []).find(
            (row) => !SIGNED_STATUSES.has(row.status) && row.status !== 'VOIDED',
        ) ?? null
    );
}

function newestSignedAt(document: DocumentFact | null): string | null {
    const dates = (document?.signatures ?? [])
        .map((row) => row.signedAt)
        .filter((value): value is string => !!value)
        .sort();
    return dates.length ? dates[dates.length - 1] : null;
}

/**
 * The WEEKLY figure: the LATEST rate row, by `starts_at`.
 *
 * `>=` AND NOT `>`, which is `advanceRepayments`' tie-break and the server's: the row recorded last
 * on a shared start date is the current rate on the day it changed.
 */
export function latestInstalment(rows: AdvanceInstalmentFact[]): AdvanceInstalmentFact | null {
    let latest: AdvanceInstalmentFact | null = null;
    for (const row of rows) {
        if (!row.startsAt) continue;
        if (latest === null || row.startsAt >= (latest.startsAt as string)) latest = row;
    }
    return latest;
}

/** The FIRST repayment: `MIN(starts_at)`, the only date this schema records for when repaying begins. */
export function firstStartsAt(rows: AdvanceInstalmentFact[]): string | null {
    let earliest: string | null = null;
    for (const row of rows) {
        if (!row.startsAt) continue;
        if (earliest === null || row.startsAt < earliest) earliest = row.startsAt;
    }
    return earliest;
}

function driverName(snapshot: AdvanceSnapshot): string {
    return isFilled(snapshot.driver.fullName) ? (snapshot.driver.fullName as string) : 'the driver';
}

/** Nothing is saved yet: the create flow, and the one state where the ceremony has no subject. */
function noAdvanceYet(snapshot: AdvanceSnapshot): boolean {
    return snapshot.isNew || !snapshot.loan;
}

// ─── The sentences the rows speak ────────────────────────────────────────────

const CANNOT_CREATE = 'The advance cannot be created without this.';
const CANNOT_GENERATE = 'The Schedule cannot be generated without this.';
/** [D-B54]'s rule, said to the admin without the id — the sentences below carry none. */
const EVERY_ADVANCE_IS_REPAID_WEEKLY =
    'Every advance is repaid weekly, and the repayment is added to the contract instalment.';
/** [D-B54]'s own sentence, and it is a statement of fact rather than a refusal. */
const PREDATES_THE_RULE = 'Settled at completion — this advance predates the weekly-schedule rule.';
const WATERMARK_UNKNOWN =
    'The recovery watermark could not be read, so no repayment can be set here yet.';
/** Not a fact about this advance either — the read that would have carried its schedule failed. */
const BOARD_UNREADABLE =
    'The agreements board could not be loaded, so this advance’s repayment cannot be read here yet.';
const HP_SIGNATURE_FIRST = 'The driver has to sign the hire purchase agreement first.';
const DRIVER_BEFORE_COUNTERSIGN =
    'The driver has to sign the hire purchase agreement before Primelane can countersign this.';
const WITNESS_BEFORE_COUNTERSIGN =
    'The witness has to sign the witness agreement before Primelane can countersign this.';
const FROZEN_UNLOCK =
    'The Schedule is signed. Void and reissue it from the agreement row to change this.';
const FILING = 'Executed · the signed PDF is still being filed.';
const NOUN = 'Operational Expense Advance';

// ─── Per-row derivation ──────────────────────────────────────────────────────

/** The shape every row derivation returns, before the shared plumbing is added. */
interface RowFacts {
    state: RowState;
    detail: string;
    note?: string | null;
    meta?: RowMeta;
}

function done(detail: string, doneDate?: string | null): RowFacts {
    return { state: 'done', detail, meta: doneDate ? { doneDate } : {} };
}

function yourMove(detail: string, note?: string): RowFacts {
    return { state: 'your-move', detail, note: note ?? null };
}

/** A record row: settled either way, with `recorded` carrying which. */
function record(recorded: boolean, detail: string, note?: string): RowFacts {
    return { state: 'done', detail, note: note ?? null, meta: { recorded } };
}

function locked(detail: string, unlockText: string): RowFacts {
    return { state: 'locked', detail, note: unlockText, meta: { unlockText } };
}

/** Whichever sentence names what is missing: the create's, or the document's. */
function blockingNote(snapshot: AdvanceSnapshot): string {
    return snapshot.isNew ? CANNOT_CREATE : CANNOT_GENERATE;
}

function transactionRow(snapshot: AdvanceSnapshot): RowFacts {
    const transaction = snapshot.transaction;
    if (!transaction) return yourMove('Not selected', blockingNote(snapshot));
    const parts = [money(transaction.amount), dateText(transaction.transactionAt)].filter(Boolean);
    return done(parts.join(' · ') || 'Linked', transaction.transactionAt);
}

function rateRow(snapshot: AdvanceSnapshot): RowFacts {
    const rate = snapshot.loan?.interestRate ?? null;
    if (rate === null || rate === undefined) return record(false, 'Not set');
    return record(true, `${rate}%`);
}

function purposeRow(snapshot: AdvanceSnapshot): RowFacts {
    const purpose = snapshot.loan?.purpose ?? null;
    if (!isFilled(purpose)) {
        return yourMove(
            'Not set',
            `The Schedule prints this as what the advance is for. ${blockingNote(snapshot)}`,
        );
    }
    return done((purpose as string).trim());
}

/**
 * The repayment pair's read-only face [D-B54], said in the terms the app can actually stand behind.
 *
 * ONE DOOR, THREE SENTENCES. Below the watermark the advance IS settled at completion and the row
 * states it as the fact it is. Where the watermark could not be read, or where the board read that
 * carries the instalment rows was refused, the row says only that — because "predates the
 * weekly-schedule rule" would be an assertion about this advance's money that nothing has
 * established, and under a refused board read it is one the app has positive reason to doubt.
 */
function legacyPair(reason: AdvanceLegacyReason): RowFacts {
    switch (reason) {
        case 'board-unreadable':
            return record(false, 'Not read', BOARD_UNREADABLE);
        case 'watermark-unknown':
            return record(false, 'Not established', WATERMARK_UNKNOWN);
        case 'below-watermark':
            return record(false, 'Settled at completion', PREDATES_THE_RULE);
    }
}

function weeklyRow(snapshot: AdvanceSnapshot): RowFacts {
    if (snapshot.legacyNoSchedule) return legacyPair(snapshot.legacyNoSchedule);
    const latest = latestInstalment(snapshot.instalments);
    const amount = latest?.amount ?? null;
    if (amount === null || amount === undefined) {
        return yourMove('Not set', `${EVERY_ADVANCE_IS_REPAID_WEEKLY} ${blockingNote(snapshot)}`);
    }
    return done(money(amount));
}

function firstRow(snapshot: AdvanceSnapshot): RowFacts {
    if (snapshot.legacyNoSchedule) return legacyPair(snapshot.legacyNoSchedule);
    const starts = firstStartsAt(snapshot.instalments);
    if (!starts) {
        return yourMove(
            'Not set',
            `The Schedule states the day repayment begins. ${blockingNote(snapshot)}`,
        );
    }
    return done(dateText(starts));
}

/**
 * The Parties row — the Schedule's own five, in one line.
 *
 * IT COUNTS WHAT IS MISSING RATHER THAN NAMING THE FIRST GAP, because all five are render-blocking
 * and an admin who fixes one wants to know how many are left. The fields themselves are edited on
 * the contract's own driver and vehicle areas, which the block beneath this row links to.
 */
function partiesRow(snapshot: AdvanceSnapshot): RowFacts {
    const fields: Array<[string, string | null]> = [
        ['the borrower’s name', snapshot.driver.fullName],
        ['their address', snapshot.driver.address],
        ['their phone', snapshot.driver.phone],
        ['the vehicle', snapshot.vehicle.model],
        ['the registration', snapshot.vehicle.registration],
    ];
    const gaps = fields.filter(([, value]) => !isFilled(value)).map(([label]) => label);
    if (gaps.length === 0) {
        const who = isFilled(snapshot.driver.fullName)
            ? (snapshot.driver.fullName as string)
            : 'On file';
        return done(who);
    }
    const missing = gaps.length === 1 ? gaps[0] : `${gaps.length} details`;
    return yourMove(`Missing ${missing}`, `The Schedule prints the Borrower and the Vehicle. ${CANNOT_GENERATE}`);
}

/**
 * The hire purchase reference — the number this Schedule quotes in three places.
 *
 * IT IS THE SERVER'S ANSWER, NOT A LOOKUP OF OUR OWN. `agreement_merge_data` resolves it from the
 * live hire purchase DOCUMENT, and [D-B52] adds the paper designation `P-<contract id>` for a contract
 * whose hire purchase agreement was signed on paper — 232 of them. A null here is a Schedule that
 * would be handed to a driver with `{{contract.hire_purchase_reference}}` printed literally [F0],
 * which is a thing to fix before generating rather than after.
 */
function hpReferenceRow(snapshot: AdvanceSnapshot): RowFacts {
    if (isFilled(snapshot.hpReference)) return done(snapshot.hpReference as string);
    return yourMove(
        'Not resolved',
        'The Schedule quotes the hire purchase agreement’s number, and this contract has none yet. ' +
            CANNOT_GENERATE,
    );
}

/**
 * THE D-B55 LOCK — the driver's hire purchase signature, which is what opens this whole ceremony.
 *
 * An in-app regular advance is gated exactly like an insurance premium advance (owner's ruling,
 * 2026-09-16): the borrower signs only after the driver's hire purchase signature, a paper row
 * satisfying it as fully as a signed seat does. The board holds the same gate on the same fact
 * (`advanceLockedFor`), so the rail and the row underneath it cannot disagree.
 *
 * IT LIFTS THE MOMENT THE ADVANCE HAS A SIGNATURE OF ITS OWN, exactly as the insurance row's does:
 * re-locking a document a party has already signed would erase their signature from the rail, and
 * voiding the hire purchase agreement underneath an advance in flight is precisely that shape.
 */
function hpSignatureRow(snapshot: AdvanceSnapshot): RowFacts {
    if (snapshot.hpDriverSigned) return done('Signed');
    if (advanceStarted(snapshot)) {
        return {
            state: 'done',
            detail: 'Not blocking',
            note: 'This advance was signed before the hire purchase signature went away, so the gate no longer holds it.',
            meta: {},
        };
    }
    return locked('Locked', HP_SIGNATURE_FIRST);
}

/** The advance already carries a signature of its own — a paper original, or a signed seat. */
function advanceStarted(snapshot: AdvanceSnapshot): boolean {
    const slot = snapshot.slot;
    if (!slot) return false;
    if (slot.legacySignedAt) return true;
    return (slot.document?.signatures ?? []).some(
        (signature) => signature.party !== 'COMPANY_REP' && SIGNED_STATUSES.has(signature.status),
    );
}

/**
 * Why Primelane cannot countersign this Schedule yet, or null.
 *
 * THE BOARD'S OWN PREDICATE, SPELLED THE SAME WAY (`siblingSignatureOutstanding`, which gained the
 * `LOAN` arm with the slot): `agreement_sign` refuses the countersignature until the driver has
 * signed the hire purchase agreement AND the witness has signed the witness agreement [D-B27, D-B55].
 * BOTH arms are cross-document on an advance — its own seats are the borrower's and Primelane's —
 * and the driver's is named first when both are outstanding, because the witness cannot sign until
 * the driver has.
 */
function countersignBlockedNote(snapshot: AdvanceSnapshot): string | null {
    if (!snapshot.hpDriverSigned) return DRIVER_BEFORE_COUNTERSIGN;
    if (!snapshot.witnessSigned) return WITNESS_BEFORE_COUNTERSIGN;
    return null;
}

/**
 * The Schedule's own state — not created, out for signature, countersigned, sealed, or paper.
 *
 * THE BORROWER SIGNS IN THE DRIVER APP AND NOT BY LINK, which is why no token is read here:
 * `agreement_issue_token` has no BORROWER path at all, so there is no "sent" to date and no open
 * count to report. The wait is named by the person, and the place they sign is named with it.
 */
function documentRow(snapshot: AdvanceSnapshot, now: Date): RowFacts {
    const slot = snapshot.slot;

    if (isPaperAdvance(slot)) {
        const signed = dateText(slot!.legacySignedAt);
        return record(true, signed ? `Signed on paper ${signed}` : 'Signed on paper');
    }

    // THE SEAL IS ASKED FIRST, exactly as `hpaRow` asks it. A finalized ceremony is a finished one,
    // and a row that went on reading `2 of 2 signed` off the signature list would hold the whole
    // area open behind a document nothing is waiting for.
    if (isAdvanceSealed(slot)) {
        const executed = dateText(slot!.legacySignedAt);
        return done(executed ? `Executed ${executed}` : 'Executed', slot!.legacySignedAt);
    }

    if (!slot || !slot.document) {
        if (noAdvanceYet(snapshot)) {
            return record(false, 'Not created', 'The Schedule is generated once the advance exists.');
        }
        if (!snapshot.hpDriverSigned && !advanceStarted(snapshot)) {
            return locked('Locked', HP_SIGNATURE_FIRST);
        }
        return yourMove('Not generated', `Generate the ${NOUN} to start the signing.`);
    }

    const document = slot.document;

    if (document.status === 'VOIDED' || document.status === 'SUPERSEDED') {
        return yourMove('Voided', `The ${NOUN} was voided. Generate it again.`);
    }

    const signed = signedCount(document);
    const total = document.signatures.length;

    if (document.status === 'COMPLETED' && !document.finalizedAt) {
        const startedAt = newestSignedAt(document);
        const elapsed = startedAt ? Math.max(0, differenceInSeconds(now, asDate(startedAt) as Date)) : 0;
        return {
            state: 'waiting',
            detail: `sealing · ${elapsed}s`,
            note: 'Signed by every party · sealing the executed PDF — this finishes on its own.',
            meta: { sealElapsedSeconds: elapsed, actor: 'Primelane' },
        };
    }

    const pending = pendingSignature(document);
    if (!pending) {
        return { state: 'waiting', detail: `${signed} of ${total} signed`, note: null, meta: {} };
    }

    if (pending.party === 'COMPANY_REP') {
        const blocked = countersignBlockedNote(snapshot);
        if (blocked) {
            return { state: 'waiting', detail: `${signed} of ${total} signed`, note: blocked, meta: {} };
        }
        return yourMove(
            `${signed} of ${total} signed`,
            'The borrower has signed. Countersign as Primelane.',
        );
    }

    // THE UNRESOLVED MERGE FIELDS ARE WORTH SAYING ONLY WHILE THEY CAN STILL BE FIXED. Nothing is
    // frozen until the first signature, so a document nobody has signed can be voided and reissued
    // against corrected figures; after that the sentence would be a complaint with no door.
    const unresolved = signed === 0 ? (snapshot.renderMissing ?? []).length : 0;
    const preflight = unresolved
        ? ` ${unresolved} ${unresolved === 1 ? 'field the Schedule quotes is' : 'fields the Schedule quotes are'} still unresolved.`
        : '';

    return {
        state: 'waiting',
        detail: `${signed} of ${total} signed`,
        note: `Waiting on ${driverName(snapshot)} to sign in the driver app.${preflight}`,
        meta: { actor: driverName(snapshot) },
    };
}

/**
 * EXECUTED — the terminal row, and the house's own seal predicate.
 *
 * THE TWO HALVES ARE BOTH THE GATE. Two production rows carry a `signed_at` with no
 * `agreement_url`, and they are not executed as far as anything that consumes the pair is
 * concerned — so the row says what is true of them rather than ticking on half the evidence.
 */
function sealedRow(snapshot: AdvanceSnapshot, now: Date): RowFacts {
    const slot = snapshot.slot;

    if (isAdvanceSealed(slot)) {
        const executed = dateText(slot!.legacySignedAt);
        const paper = isPaperAdvance(slot);
        return {
            state: 'done',
            detail: executed ? `Executed ${executed}` : 'Executed',
            note: paper ? 'Signed on paper.' : null,
            meta: { doneDate: executed },
        };
    }

    if (slot?.legacySignedAt) {
        return { state: 'waiting', detail: 'Filing', note: FILING, meta: { actor: 'Primelane' } };
    }

    const document = slot?.document ?? null;
    if (document && document.status === 'COMPLETED' && !document.finalizedAt) {
        const startedAt = newestSignedAt(document);
        const elapsed = startedAt ? Math.max(0, differenceInSeconds(now, asDate(startedAt) as Date)) : 0;
        return {
            state: 'waiting',
            detail: `sealing · ${elapsed}s`,
            note: 'Sealing the executed PDF — this finishes on its own.',
            meta: { sealElapsedSeconds: elapsed, actor: 'Primelane' },
        };
    }

    return locked('Not executed', 'Both parties have to sign the Schedule.');
}

function rowFacts(def: AdvanceRowDef, snapshot: AdvanceSnapshot, now: Date): RowFacts {
    switch (def.id) {
        case 'adv-transaction':
            return transactionRow(snapshot);
        case 'adv-rate':
            return rateRow(snapshot);
        case 'adv-purpose':
            return purposeRow(snapshot);
        case 'adv-weekly':
            return weeklyRow(snapshot);
        case 'adv-first':
            return firstRow(snapshot);
        case 'adv-parties':
            return partiesRow(snapshot);
        case 'adv-hp-reference':
            return hpReferenceRow(snapshot);
        case 'adv-hp-signature':
            return hpSignatureRow(snapshot);
        case 'adv-document':
            return documentRow(snapshot, now);
        case 'adv-sealed':
            return sealedRow(snapshot, now);
    }
}

/**
 * THE CLASS IS THE ROW'S DEFAULT ANSWER AND THIS READS THE ADVANCE ON TOP OF IT — `rowClassOf`'s
 * rule from the contract rail, applied to the two shapes that have one.
 *
 * THE REPAYMENT PAIR FALLS TO `record` ON A PRE-WATERMARK ADVANCE WITH NO SCHEDULE [D-B54]. Nothing is
 * owed there and nothing anybody types can change it, so the pair states a fact about the advance
 * instead of a figure somebody has yet to provide — which is exactly what the record class means.
 *
 * THE AGREEMENT ROW FALLS TO `record` AT BOTH ENDS OF ITS LIFE: before the advance exists there is
 * no subject to generate a Schedule over, and on one of the 161 paper originals [F3] there never
 * will be one. In between it is a `render` row like any other document.
 */
function rowClassOf(def: AdvanceRowDef, snapshot: AdvanceSnapshot): RowClass {
    if (snapshot.legacyNoSchedule && (def.id === 'adv-weekly' || def.id === 'adv-first')) {
        return 'record';
    }
    if (def.id === 'adv-document' && (noAdvanceYet(snapshot) || isPaperAdvance(snapshot.slot))) {
        return 'record';
    }
    return def.cls;
}

/**
 * DORMANT: nothing is owed on this row and nothing the admin types can change that, so it is out of
 * both halves of its group's counter and wears the grey "nothing to do here yet" mark.
 *
 * TWO ADVANCE SHAPES DECLARE IT, and both are the class rule above read once more. The legacy
 * repayment pair [D-B54] is the whole of the first: `0/2` over two lines nobody owes is the counter
 * describing a list nobody is looking at, and the group reads `Settled at completion` instead. The
 * second is the agreement row before the advance exists — a document whose subject has not been
 * created cannot be overdue, which is the one exclusion this counter has ever had.
 *
 * THE EXECUTED GATE IS NOT ONE OF THEM, deliberately: every advance created from here owes an
 * executed Schedule, so the row stays counted and locked rather than disappearing out of the
 * denominator on the very flow that exists to paper it.
 */
function rowDormantOf(def: AdvanceRowDef, snapshot: AdvanceSnapshot): boolean {
    if (snapshot.legacyNoSchedule && (def.id === 'adv-weekly' || def.id === 'adv-first')) return true;
    return def.id === 'adv-document' && noAdvanceYet(snapshot);
}

/**
 * THE FROZEN DEMOTION — a figure the signed Schedule printed is not the admin's move any more.
 *
 * Once `frozen_at` is set the disbursement and repayment areas render read-only, so a blue dot over
 * a row nobody can type into would be the rail pointing at a door the canvas has shut. The
 * correction is void-and-reissue on the agreement row, and the unlock text says so rather than
 * leaving an admin to guess. Rows already settled keep their receipts: freezing does not un-record
 * what is on file.
 */
function freeze(row: RowFacts, def: AdvanceRowDef, snapshot: AdvanceSnapshot): RowFacts {
    if (!snapshot.frozen) return row;
    if (def.area === 'agreement') return row;
    if (row.state === 'done') return row;
    return locked(row.detail, FROZEN_UNLOCK);
}

// ─── The whole rail ──────────────────────────────────────────────────────────

function receiptFor(area: AdvanceArea, snapshot: AdvanceSnapshot): string {
    if (area === 'repayment' && snapshot.legacyNoSchedule) {
        switch (snapshot.legacyNoSchedule) {
            case 'board-unreadable':
                return 'Not read';
            case 'watermark-unknown':
                return 'Not established';
            case 'below-watermark':
                return 'Settled at completion';
        }
    }
    if (area === 'agreement') {
        const executed = dateText(snapshot.slot?.legacySignedAt);
        return executed ? `Executed ${executed}` : AREA_RECEIPTS.agreement;
    }
    return AREA_RECEIPTS[area];
}

/**
 * The one sentence under the advance's name, and what to do about it.
 *
 * THE PRECEDENCE IS THE CEREMONY'S OWN ORDER, READ BACKWARDS: what is finished outranks what is in
 * flight, which outranks what has not started — so the band always names the furthest point the
 * advance has actually reached rather than the first unfilled thing on the rail.
 *
 * "FIGURES ARE LOCKED." PREFIXES THE SENTENCE RATHER THAN REPLACING IT, and only while the advance
 * is still in flight: an executed Schedule's figures have been locked since the first signature,
 * and saying so over "Executed Sep 12, 2026." would spend the one line on the page that changes
 * telling an admin something the date beside it already implies.
 */
function narratorFor(snapshot: AdvanceSnapshot): AdvanceNarrator {
    const slot = snapshot.slot;
    const openAgreement: AdvanceNarratorAction = {
        id: 'open-advance-agreement',
        label: 'Open the agreement',
        kind: 'primary',
        intent: 'jump',
        rowId: 'adv-document',
        area: 'agreement',
    };

    if (isPaperAdvance(slot)) {
        const signed = dateText(slot!.legacySignedAt);
        return {
            sentence: signed ? `Signed on paper on ${signed}.` : 'Signed on paper.',
            actions: [],
        };
    }

    if (isAdvanceSealed(slot)) {
        const executed = dateText(slot!.legacySignedAt);
        return { sentence: executed ? `Executed ${executed}.` : 'Executed.', actions: [] };
    }

    const prefix = (sentence: string) =>
        snapshot.frozen ? `Figures are locked. ${sentence}` : sentence;

    const document = slot?.document ?? null;
    if (document && document.status !== 'VOIDED' && document.status !== 'SUPERSEDED') {
        if (document.status === 'COMPLETED' && !document.finalizedAt) {
            return { sentence: prefix('Sealing the executed PDF.'), actions: [] };
        }
        if (slot?.legacySignedAt) {
            return { sentence: prefix(FILING), actions: [] };
        }
        const pending = pendingSignature(document);
        if (pending?.party === 'COMPANY_REP') {
            const blocked = countersignBlockedNote(snapshot);
            return {
                sentence: prefix(blocked ?? 'Ready for Primelane’s countersignature.'),
                actions: blocked ? [] : [openAgreement],
            };
        }
        if (pending) {
            return {
                sentence: prefix(`Waiting on ${driverName(snapshot)} to sign in the driver app.`),
                actions: [],
            };
        }
        // Every seat signed and the document not yet COMPLETED — the seal is what follows.
        return { sentence: prefix('Sealing the executed PDF.'), actions: [] };
    }

    if (noAdvanceYet(snapshot)) {
        return {
            sentence: 'Set out what Primelane paid and how it comes back, then create the advance.',
            actions: [],
        };
    }

    if (!snapshot.hpDriverSigned && !advanceStarted(snapshot)) {
        return { sentence: prefix(HP_SIGNATURE_FIRST), actions: [] };
    }

    return {
        sentence: prefix('Created. Generate the Schedule when the figures are right.'),
        actions: [openAgreement],
    };
}

/**
 * One snapshot in, the whole rail out.
 *
 * Nothing here reads a clock, a query cache or the DOM.
 */
export function computeAdvanceState(snapshot: AdvanceSnapshot): AdvanceState {
    const now = asDate(snapshot.now) ?? new Date();
    const drafts = snapshot.drafts ?? {};

    const rows: AdvanceRow[] = ROW_DEFS.map((def) => {
        const facts = freeze(rowFacts(def, snapshot, now), def, snapshot);
        const draft = drafts[def.id];
        const meta: RowMeta = { ...(facts.meta ?? {}), ...(draft ? { draft } : {}) };
        return {
            id: def.id,
            area: def.area,
            label: def.label,
            cls: rowClassOf(def, snapshot),
            optional: rowDormantOf(def, snapshot),
            state: facts.state,
            detail: facts.detail,
            note: facts.note ?? null,
            meta,
            jump: { area: def.area, block: def.block, ...(def.focus ? { focus: def.focus } : {}) },
            // NO SERVER REFUSAL LANDS ON THIS RAIL. `sign_contract` grades a contract; an advance is
            // written with two ordinary inserts, whose failures are reported where they happen.
            refusal: null,
        };
    });

    const areaGroups: AdvanceAreaGroup[] = ADVANCE_AREAS.map((area, index) => {
        const groupRows = rows.filter((row) => row.area === area.key);
        const counted = groupRows.filter(isCountedRow);
        const satisfied = groupRows.every((row) => row.state === 'done');
        return {
            area: area.key,
            label: area.label,
            index: index + 1,
            rows: groupRows,
            doneCount: counted.filter(isRowSettled).length,
            requiredCount: counted.length,
            satisfied,
            receipt: satisfied ? receiptFor(area.key, snapshot) : null,
            // NO SERVER n-OF-m. `get_contract_setup_status` grades a contract's setup and knows
            // nothing about one advance, so the rail's own count is the only count there is.
            serverCounts: null,
        };
    });

    const progress: SetupProgress = {
        areasComplete: areaGroups.filter((group) => group.satisfied).length,
        areaCount: areaGroups.length,
        needYou: rows.filter((row) => row.state === 'your-move').length,
        waitingOnOthers: rows.filter((row) => row.state === 'waiting').length,
        outstanding: rows.filter((row) => row.state !== 'done').length,
    };

    return { rows, areaGroups, progress, narrator: narratorFor(snapshot) };
}

/**
 * The area an admin who arrived without one should be put in front of.
 *
 * THE WORK'S OWN ORDER, AND NOTHING CLEVERER. An advance has three areas in the sequence the
 * Schedule states them, and the first one carrying something to do is where the page opens; with
 * nothing to do at all it opens on whatever is still in flight, and failing that on the agreement,
 * which is where a finished advance's record lives.
 */
export function resolveAdvanceLandingArea(state: AdvanceState): AdvanceArea {
    const startable = state.rows.find((row) => row.state === 'your-move');
    if (startable) return startable.area;
    const inFlight = state.rows.find((row) => row.state === 'waiting' || row.state === 'locked');
    if (inFlight) return inFlight.area;
    return 'agreement';
}
